import { useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import { Heart, Highlighter } from 'lucide-react';

import { EmbedZapButton } from '@/components/EmbedZapButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAddressableEvent, useEventById } from '@/hooks/useAddressableEvent';
import { useAuthor } from '@/hooks/useAuthor';
import { useEngagement } from '@/hooks/useEngagement';
import { getDisplayName } from '@/lib/getDisplayName';
import { parseHighlight, primarySource } from '@/lib/highlights';
import { relativeTime } from '@/lib/relativeTime';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

export function EmbedPage() {
  const { nip19: identifier } = useParams<{ nip19: string }>();
  const [searchParams] = useSearchParams();
  const { nostr } = useNostr();
  const cardRef = useRef<HTMLDivElement>(null);

  // Instant-paint params embedded in the share URL.
  const paramQuote = searchParams.get('q');
  const paramBy = searchParams.get('by');

  // Theme: ?theme=dark|light, defaulting to the host visitor's preference.
  useEffect(() => {
    const theme = searchParams.get('theme');
    const dark = theme === 'dark' || (!theme && window.matchMedia('(prefers-color-scheme: dark)').matches);
    document.documentElement.classList.toggle('dark', dark);
    return () => document.documentElement.classList.remove('dark');
  }, [searchParams]);

  // Decode the identifier (note1 or nevent1), keeping any relay hints.
  const { eventId, relayHints } = useMemo(() => {
    if (!identifier) return { eventId: null, relayHints: [] as string[] };
    try {
      const decoded = nip19.decode(identifier);
      if (decoded.type === 'note') return { eventId: decoded.data, relayHints: [] as string[] };
      if (decoded.type === 'nevent') {
        return { eventId: decoded.data.id, relayHints: decoded.data.relays ?? [] };
      }
    } catch {
      return { eventId: null, relayHints: [] as string[] };
    }
    return { eventId: null, relayHints: [] as string[] };
  }, [identifier]);

  const eventQuery = useEventById(eventId ?? '');

  // Embed viewers are anonymous: if the pool (app relays) doesn't have the
  // event, fall back to querying the nevent's relay hints directly.
  const hintedQuery = useQuery({
    queryKey: ['nostr', 'event-by-id-hinted', eventId ?? '', relayHints.join(',')],
    queryFn: async () => {
      if (!eventId) return null;
      const group = nostr.group(relayHints);
      const events = await group.query([{ ids: [eventId], limit: 1 }], {
        signal: AbortSignal.timeout(5000),
      });
      return events[0] ?? null;
    },
    enabled: !!eventId && !eventQuery.isPending && !eventQuery.data && relayHints.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  const highlight = useMemo(() => {
    const event = eventQuery.data ?? hintedQuery.data;
    if (!event) return null;
    return parseHighlight(event);
  }, [eventQuery.data, hintedQuery.data]);

  const author = useAuthor(highlight?.event.pubkey);
  const highlights = useMemo(() => (highlight ? [highlight] : []), [highlight]);
  const engagement = useEngagement(highlights);

  // Resolve an article source for the attribution line.
  const source = highlight ? primarySource(highlight.sources) : null;
  const articleCoord = source?.type === 'article' ? source : null;
  const articleEvent = useAddressableEvent(
    articleCoord?.kind ?? 0,
    articleCoord?.pubkey ?? '',
    articleCoord?.identifier ?? '',
  );

  const origin = window.location.origin;

  // What we can show instantly vs. after hydration.
  const quote = highlight?.quote || paramQuote || '';
  const highlighterPubkey = highlight?.event.pubkey;
  const highlighterName = highlighterPubkey
    ? getDisplayName(author.data?.metadata, highlighterPubkey)
    : paramBy || 'Someone';
  const highlighterAvatar = sanitizeUrl(author.data?.metadata?.picture);
  const createdAt = highlight?.event.created_at;
  const stats = highlight ? engagement.data?.get(highlight.event.id) : undefined;

  const permalink = highlight
    ? `${origin}/${nip19.neventEncode({ id: highlight.event.id, author: highlight.event.pubkey })}`
    : `${origin}/`;

  // Source attribution line.
  let sourceLabel: string | null = null;
  let sourceHref: string | null = null;
  if (source?.type === 'article') {
    const title = articleEvent.data?.tags.find(([n]) => n === 'title')?.[1]?.trim();
    sourceLabel = title || 'an article';
    sourceHref = `${origin}/${nip19.naddrEncode({ kind: source.kind, pubkey: source.pubkey, identifier: source.identifier })}`;
  } else if (source?.type === 'note') {
    sourceLabel = 'a note';
    sourceHref = `${origin}/${nip19.noteEncode(source.id)}`;
  } else if (source?.type === 'url') {
    const href = sanitizeUrl(source.url);
    if (href) {
      sourceLabel = new URL(href).hostname;
      sourceHref = href;
    }
  }

  const showSkeleton = !quote && (eventQuery.isPending || hintedQuery.isPending);

  // Auto-height: report content height to the host page (paired with the
  // listener script in buildEmbedSnippet — platforms like Ghost allow it).
  useEffect(() => {
    if (window.parent === window || !eventId) return; // not embedded
    const el = cardRef.current;
    if (!el) return;

    const id = eventId.slice(0, 12);
    const post = () => {
      const height = Math.ceil(el.getBoundingClientRect().height + 20);
      window.parent.postMessage({ type: 'glowmark:embed', id, height }, '*');
    };

    post();
    const observer = new ResizeObserver(post);
    observer.observe(el);
    return () => observer.disconnect();
  }, [eventId]);

  return (
    <div className="bg-background p-2.5 font-sans text-foreground">
      <div ref={cardRef} className="rounded-xl border border-border bg-card p-4 space-y-3 shadow-sm">
        {/* Quote */}
        {showSkeleton ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-4 w-full rounded bg-muted" />
            <div className="h-4 w-4/5 rounded bg-muted" />
          </div>
        ) : quote ? (
          <blockquote className="border-l-4 border-primary/70 pl-3 font-serif leading-relaxed line-clamp-6">
            {quote}
          </blockquote>
        ) : (
          <p className="text-sm text-muted-foreground">Highlight unavailable.</p>
        )}

        {/* Highlighter + source */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
          <Avatar className="size-5 border border-border">
            {highlighterAvatar && <AvatarImage src={highlighterAvatar} alt="" loading="lazy" />}
            <AvatarFallback className="bg-secondary text-[9px] font-semibold">
              {highlighterName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="truncate font-medium text-foreground/80">{highlighterName}</span>
          <span className="shrink-0">highlighted</span>
          {sourceHref && sourceLabel && (
            <>
              <span className="shrink-0">from</span>
              <a
                href={sourceHref}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate hover:underline underline-offset-2 hover:text-foreground"
              >
                {sourceLabel}
              </a>
            </>
          )}
          {createdAt && (
            <time className="shrink-0" dateTime={new Date(createdAt * 1000).toISOString()}>
              · {relativeTime(createdAt)}
            </time>
          )}
        </div>

        {/* Footer: zap + counts + brand */}
        <div className="flex items-start gap-3 pt-1 border-t border-border/60">
          <div className="min-w-0 flex-1">
            {highlight && (
              <EmbedZapButton highlight={highlight} totalSats={stats?.zapSats} permalink={permalink} />
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
            {stats && stats.reactions > 0 && (
              <span className="inline-flex items-center gap-1">
                <Heart className="size-3.5" />
                {stats.reactions}
              </span>
            )}
            <a
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium hover:text-foreground transition-colors"
            >
              <Highlighter className="size-3.5 text-primary" />
              glowmark
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmbedPage;
